import React, { useRef, useState } from "react";
import { toBlob } from "html-to-image";
import { toast } from "sonner";

const accentFor = (s) => (s >= 70 ? "#37d39a" : s >= 45 ? "#ffce4d" : "#ff6b5e");

const ShareCard = ({ mood, weather, story }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const score = Math.max(0, Math.min(mood?.mood_score ?? 0, 100));
  const label = (mood?.mood_text || "Steady").toUpperCase();
  const accent = accentFor(score);
  const deg = Math.round((score / 100) * 360);
  const summary = mood?.summary || (mood?.now && mood.now[0]) || "The island is doing its thing.";
  const temp = weather?.temperature_c != null ? Math.round(weather.temperature_c) : null;
  const condition = weather?.condition || "—";
  const feels = weather?.feels_like_c != null ? Math.round(weather.feels_like_c) : null;
  const headline = story?.title || "Quiet news day across the island.";
  const src = story?.source ? `${story.source}${story.minutes_ago != null ? ` · ${story.minutes_ago}m ago` : ""}` : "Barbados Live";
  const dateStr = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: "America/Barbados" });

  const caption = `Barbados feels ${label} (${score}/100) right now${temp != null ? ` — ${condition}, ${temp}°C` : ""}. Top story: “${headline}”. #Barbados #BarbadosLive`;

  const share = async () => {
    if (!ref.current || busy) return;
    setBusy(true);
    try {
      const blob = await toBlob(ref.current, {
        pixelRatio: 2, cacheBust: true, skipFonts: true,
        backgroundColor: "#04101f", width: 1080, height: 1080,
      });
      if (!blob) throw new Error("empty");
      const file = new File([blob], "barbados-vibe.png", { type: "image/png" });

      // Mobile: native share sheet with the image
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "Barbados Live", text: caption });
          setBusy(false);
          return;
        } catch (err) {
          if (err?.name === "AbortError") { setBusy(false); return; }
          // otherwise fall through to download
        }
      }

      // Desktop / fallback: reliable blob download + copy caption
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "barbados-vibe.png";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      try { await navigator.clipboard.writeText(caption); } catch { /* noop */ }
      toast.success("Vibe card downloaded", { description: "Caption copied — post it anywhere!" });
    } catch (e) {
      toast.error("Couldn't create the card — please try again.");
    }
    setBusy(false);
  };

  return (
    <>
      <button className="btn-share" onClick={share} disabled={busy} data-testid="share-card-btn">
        <i className={`fa-solid ${busy ? "fa-spinner fa-spin" : "fa-share-nodes"}`} /> {busy ? "Creating…" : "Share vibe"}
      </button>

      {/* off-screen 1080×1080 card that gets rasterised */}
      <div style={{ position: "fixed", top: 0, left: "-99999px", pointerEvents: "none" }} aria-hidden="true">
        <div ref={ref} data-testid="share-card" style={{
          width: 1080, height: 1080, padding: 72, boxSizing: "border-box", position: "relative", overflow: "hidden",
          background: "radial-gradient(1100px 700px at 84% -10%, rgba(255,199,44,0.16), transparent 55%), radial-gradient(1000px 700px at -6% 0%, rgba(0,229,255,0.18), transparent 55%), linear-gradient(160deg, #071228 0%, #020509 100%)",
          fontFamily: "'Barlow Semi Condensed', system-ui, sans-serif", color: "#f1f5f9",
          display: "flex", flexDirection: "column", justifyContent: "space-between",
        }}>
          <div style={{
            position: "absolute", inset: 0,
            backgroundImage: "linear-gradient(rgba(0,229,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(0,229,255,0.06) 1px, transparent 1px)",
            backgroundSize: "60px 60px", maskImage: "radial-gradient(120% 90% at 50% 0%, #000 55%, transparent 100%)", WebkitMaskImage: "radial-gradient(120% 90% at 50% 0%, #000 55%, transparent 100%)",
          }} />

          {/* brand row */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
              <div style={{ width: 74, height: 74, borderRadius: 20, background: "linear-gradient(135deg,#ffd85e,#d9a413)", boxShadow: "0 0 40px rgba(255,199,44,0.5)" }} />
              <div>
                <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 800, fontSize: 40, letterSpacing: 2, lineHeight: 1 }}>BARBADOS LIVE</div>
                <div style={{ fontWeight: 700, fontSize: 20, letterSpacing: 4, color: "#ffc72c", marginTop: 8 }}>ISLAND PULSE</div>
              </div>
            </div>
            <div style={{ textAlign: "right", color: "#9fe9ff", fontSize: 24, fontWeight: 600 }}>{dateStr}</div>
          </div>

          {/* mood centrepiece */}
          <div style={{ display: "flex", alignItems: "center", gap: 56, position: "relative" }}>
            <div style={{ width: 300, height: 300, borderRadius: "50%", flexShrink: 0, display: "grid", placeItems: "center", background: `conic-gradient(${accent} ${deg}deg, rgba(255,255,255,0.08) ${deg}deg)`, boxShadow: `0 0 60px ${accent}44` }}>
              <div style={{ width: 232, height: 232, borderRadius: "50%", background: "#04101f", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, fontSize: 104, lineHeight: 1, color: "#fff" }}>{score}</div>
                <div style={{ fontSize: 24, color: "#93a7c4", letterSpacing: 2, marginTop: 4 }}>ISLAND MOOD</div>
              </div>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 800, fontSize: 64, color: accent, letterSpacing: 2, lineHeight: 1 }}>{label}</div>
              <div style={{ fontSize: 34, lineHeight: 1.35, marginTop: 24, color: "#e6eefb", fontWeight: 500 }}>{summary}</div>
            </div>
          </div>

          {/* weather + top story */}
          <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: 26 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 28, padding: "26px 32px", borderRadius: 22, background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.22)" }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, fontSize: 72, color: "#fff", lineHeight: 1 }}>{temp != null ? `${temp}°` : "—"}</div>
              <div>
                <div style={{ fontSize: 34, fontWeight: 700 }}>{condition}</div>
                <div style={{ fontSize: 24, color: "#9fe9ff", marginTop: 6 }}>{feels != null ? `Feels ${feels}° · ` : ""}{weather?.coverage_label || "Barbados"}</div>
              </div>
            </div>
            <div style={{ padding: "26px 32px", borderRadius: 22, background: "rgba(255,199,44,0.06)", border: "1px solid rgba(255,199,44,0.24)" }}>
              <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: 3, color: "#ffc72c" }}>TOP STORY</div>
              <div style={{ fontSize: 34, fontWeight: 700, lineHeight: 1.25, marginTop: 12, color: "#fff" }}>{headline}</div>
              <div style={{ fontSize: 22, color: "#93a7c4", marginTop: 12 }}>{src}</div>
            </div>
          </div>

          <div style={{ position: "relative", textAlign: "center", fontSize: 22, color: "#6f88a8", letterSpacing: 1 }}>
            Live island vibe · what&apos;s happening in Barbados right now
          </div>
        </div>
      </div>
    </>
  );
};

export default ShareCard;
